import { Button, Descriptions, Drawer, Image, Popconfirm, Space, Table, Tag, Typography } from "antd";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

import { DishIssueStatus } from "@fitness-app/data-models/entities/DishIssue";

import { buildCorrectionRows, type CorrectionRow } from "./ingredientCorrectionRows";
import {
  getIssueDish,
  getIssueIngredient,
  getIssueName,
  getProductNutrientsLine,
  getTraineeIssueMetadata,
  getTraineeIssuePhotoUrl,
  getTraineeName,
  hasProposedChanges,
  INGREDIENT_ISSUE_CATEGORY_LABELS,
  isTraineeDishIssue,
  type DishIssueListItem,
} from "./ingredientIssues";

interface TraineeIssueDrawerProps {
  issue: DishIssueListItem | null;
  loading: boolean;
  onClose: () => void;
  onApplyProposal: (issue: DishIssueListItem) => void;
  onEditProduct: (issue: DishIssueListItem) => void;
  onEditDish: (issue: DishIssueListItem) => void;
  onResolveWithoutChanges: (issue: DishIssueListItem) => void;
  onReject: (issue: DishIssueListItem) => void;
}

const Thumbnail = ({ url }: { url: string | null }) =>
  url ? <Image src={url} width={64} height={64} style={{ objectFit: "cover" }} /> : <span>-</span>;

const renderValue = (row: CorrectionRow, side: "before" | "after") =>
  row.kind === "image" ? <Thumbnail url={row[side]} /> : row[side];

/** A client's report, of a product or of a dish; the product ones also carry her proposed changes. */
export const TraineeIssueDrawer = ({
  issue,
  loading,
  onClose,
  onApplyProposal,
  onEditProduct,
  onEditDish,
  onResolveWithoutChanges,
  onReject,
}: TraineeIssueDrawerProps) => {
  const { t } = useTranslation("nutrition");
  const metadata = issue ? getTraineeIssueMetadata(issue) : null;
  const rows =
    metadata?.proposedChanges && Object.keys(metadata.proposedChanges).length
      ? buildCorrectionRows(metadata.before ?? {}, metadata.proposedChanges, (key) => t(`nutrients.${key}`))
      : [];
  const isOpen = issue?.status === DishIssueStatus.Reported;
  const isDishIssue = issue ? isTraineeDishIssue(issue) : false;
  const photoUrl = issue ? getTraineeIssuePhotoUrl(issue) : null;
  // The catalog row as it is now, so the trainer knows which of the same-named products it is.
  const product = issue && !isDishIssue ? getIssueIngredient(issue) : null;

  return (
    <Drawer
      title={issue ? getIssueName(issue) : ""}
      placement="right"
      open={Boolean(issue)}
      onClose={onClose}
      width={640}
      destroyOnClose
      footer={
        issue && isOpen ? (
          <Space wrap>
            {hasProposedChanges(issue) && (
              <Button type="primary" loading={loading} onClick={() => onApplyProposal(issue)}>
                Zastosuj zmiany
              </Button>
            )}
            {isDishIssue ? (
              <Button type="primary" disabled={loading || !getIssueDish(issue)} onClick={() => onEditDish(issue)}>
                Edytuj
              </Button>
            ) : (
              <Button disabled={loading || !getIssueIngredient(issue)} onClick={() => onEditProduct(issue)}>
                Popraw produkt
              </Button>
            )}
            <Button disabled={loading} onClick={() => onResolveWithoutChanges(issue)}>
              Rozwiązane bez zmian
            </Button>
            <Popconfirm
              title="Odrzucić zgłoszenie?"
              okText="Odrzuć"
              cancelText="Anuluj"
              okButtonProps={{ danger: true }}
              onConfirm={() => onReject(issue)}
            >
              <Button danger disabled={loading}>
                Odrzuć
              </Button>
            </Popconfirm>
          </Space>
        ) : null
      }
    >
      {issue ? (
        <Space direction="vertical" size={24} className="w-full">
          <Descriptions column={1} size="small" bordered>
            <Descriptions.Item label="Klient">{getTraineeName(issue.trainee) || "-"}</Descriptions.Item>
            <Descriptions.Item label="Kategoria">
              {issue.categories?.length
                ? issue.categories.map((category) => (
                    <Tag key={category}>{INGREDIENT_ISSUE_CATEGORY_LABELS[category]}</Tag>
                  ))
                : "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Data zgłoszenia">
              {dayjs(issue.createdAt).format("DD.MM.YYYY HH:mm")}
            </Descriptions.Item>
            <Descriptions.Item label="Komentarz">
              {issue.reason ? (
                <Typography.Paragraph className="!mb-0 whitespace-pre-line">{issue.reason}</Typography.Paragraph>
              ) : (
                "-"
              )}
            </Descriptions.Item>
            {photoUrl ? (
              <Descriptions.Item label="Zdjęcie">
                <Image src={photoUrl} width={160} />
              </Descriptions.Item>
            ) : null}
          </Descriptions>

          {product ? (
            <Descriptions title="Produkt" column={1} size="small" bordered>
              <Descriptions.Item label="Zdjęcie">
                <Thumbnail url={product.image} />
              </Descriptions.Item>
              <Descriptions.Item label="Nazwa">{product.name}</Descriptions.Item>
              <Descriptions.Item label="Marka">{product.brand || "-"}</Descriptions.Item>
              <Descriptions.Item label="Kod kreskowy">{product.barCode || "-"}</Descriptions.Item>
              <Descriptions.Item label={`Na 100 ${product.unit || "g"}`}>
                {getProductNutrientsLine(product) || "-"}
              </Descriptions.Item>
            </Descriptions>
          ) : null}

          {rows.length ? (
            <Table<CorrectionRow> dataSource={rows} rowKey="key" pagination={false} size="small">
              <Table.Column<CorrectionRow> title="Pole" dataIndex="label" />
              <Table.Column<CorrectionRow> title="Przed" render={(_, row) => renderValue(row, "before")} />
              <Table.Column<CorrectionRow>
                title="Propozycja"
                render={(_, row) => <span className="font-medium">{renderValue(row, "after")}</span>}
              />
            </Table>
          ) : null}
        </Space>
      ) : null}
    </Drawer>
  );
};
